import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import {
  AddressTag, AppliedToplineItem, GetTagCategoryByIdGQL, TagCategoryInfoFragment,
  TaggableLogbookDetailsFragment, TagType
} from 'generated/graphql';

import { getSafe } from 'helpers';
import { orderBy } from 'lodash-es';
import { AddressPipe } from 'shared/pipes/address.pipe';
import { ToplineAddClickedEvent } from '../ToplineAddClickedEvent';

@Component({
  selector: 'app-topline-tag-category',
  templateUrl: './topline-tag-category.component.html',
  styleUrls: ['./topline-tag-category.component.scss']
})
export class ToplineTagCategoryComponent implements OnInit, OnChanges {
  addressPipe = new AddressPipe();

  @Input() topline: Partial<AppliedToplineItem> | any;
  @Input() targetId: string;
  @Input() targetType: 'Entity' | 'EntityConnection';

  /** Will be used for top topline */
  @Input() truncate: boolean;
  @Output() addClick = new EventEmitter<ToplineAddClickedEvent>();

  private _tagCategory: TagCategoryInfoFragment;
  get tagCategory() {
    return this._tagCategory;
  }

  get tagCategoryName() {
    return this._tagCategory?.name;
  }

  get attachmentsEnabled() {
    return this._tagCategory?.attachmentsEnabled;
  }

  get firstAppliedTag(): TaggableLogbookDetailsFragment {
    const appTags = orderBy(this.topline.appliedToplineValue?.appliedTags || [], ['createdAt'], ['desc']);
    if (appTags?.length > 0) {
      return appTags[0];
    }
    return null;
  }

  constructor(
    private _getTagCategoryGQL: GetTagCategoryByIdGQL,
    private _cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadTagCategory();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.topline && !changes.topline.firstChange) {
      this.loadTagCategory();
    }
  }

  private loadTagCategory(): void {
    const tagCategoryId = this.topline?.toplineItem?.itemId;

    // Tag category unchanged - no need to refetch
    if (this._tagCategory && tagCategoryId === this._tagCategory.id) return;

    // Tag category changed - reset to avoid showing stale label while fetching
    this._tagCategory = null;

    // Fetch new tag category
    this._getTagCategoryGQL.fetch({
      tagCategoryId
    }).subscribe(({ data }) => {
      this._tagCategory = data.getTagCategoryById;
      this._cd.markForCheck();
    });
  }

  preventDefault(event: MouseEvent) {
    event.stopImmediatePropagation();
  }

  onAddClicked() {
    const eventData = { targetType: this.targetType, targetId: this.targetId, topline: this.topline };
    this.addClick.emit(eventData);
  }

  getHighlightedTooltip() {
    const tagType = this.firstAppliedTag?.tag.tagType;
    if (tagType === TagType.Address) {
      const tag = this.firstAppliedTag?.tag as AddressTag;
      return this.addressPipe.transform(tag.address);
    }
    if (tagType === TagType.Date) {
      const note = getSafe(() => this.firstAppliedTag?.notes[0], null);
      if (note == null) {
        return null;
      }
      return new DatePipe('en-us').transform(note.text, 'shortDate');
    }
    if (tagType === TagType.Number) {
      const note = getSafe(() => this.firstAppliedTag?.notes[0], null);
      if (note == null) {
        return null;
      }
      return new DecimalPipe('en-us').transform(note.text);
    }
    if (tagType === TagType.Notes) {
      const note = getSafe(() => this.firstAppliedTag?.notes[0], null);
      return note?.text;
    }
  }
}
